/**
 * Simple Token Expiration Checker
 * Standalone utility for checking PASETO token expiration without dependencies
 */

import {
  validateToken,
  isTokenExpired,
  getStoredToken,
  TOKEN_KEYS,
  DEFAULT_EXPIRATION_BUFFER_MS,
  decodeTokenPayload
} from './token-utils';

/**
 * Simple token checker that can be used anywhere without circular dependencies
 */
export class TokenChecker {
  /**
   * Check if the current stored token is expired
   *
   * @param bufferMs - Buffer time before expiration (default: 5 minutes)
   * @returns True if token is expired or will expire within buffer time
   */
  static isExpired(bufferMs: number = DEFAULT_EXPIRATION_BUFFER_MS): boolean {
    const token = getStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
    if (!token) {
      return true;
    }

    return isTokenExpired(token, bufferMs);
  }

  /**
   * Get comprehensive token validation status
   *
   * @param bufferMs - Buffer time before expiration
   * @returns Validation result with detailed status
   */
  static getValidationStatus(bufferMs: number = DEFAULT_EXPIRATION_BUFFER_MS) {
    const token = getStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
    const provisionalToken = getStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);

    // If we have a provisional token but no access token, assume it's valid for MFA operations
    // We can't validate encrypted provisional tokens client-side
    if (!token && provisionalToken) {
      return {
        hasToken: true,
        isValid: true, // Assume valid for MFA operations - backend will validate
        isExpired: false,
        isExpiringSoon: false,
        secondsUntilExpiration: 600, // 10 minutes as set in backend
        error: null
      };
    }

    if (!token) {
      return {
        hasToken: false,
        isValid: false,
        isExpired: true,
        isExpiringSoon: false,
        secondsUntilExpiration: -1,
        error: 'No token found'
      };
    }

    // Only try to validate if it's a public token (can be decoded client-side)
    const tokenParts = token.split('.');
    if (tokenParts.length >= 2 && tokenParts[1] === 'local') {
      // This is an encrypted token, can't validate client-side
      return {
        hasToken: true,
        isValid: true, // Assume valid - backend will validate
        isExpired: false,
        isExpiringSoon: false,
        secondsUntilExpiration: 600,
        error: null
      };
    }

    const validation = validateToken(token, bufferMs);
    return {
      hasToken: true,
      isValid: validation.isValid,
      isExpired: validation.isExpired,
      isExpiringSoon: validation.isExpiringSoon,
      secondsUntilExpiration: validation.secondsUntilExpiration,
      error: validation.error
    };
  }

  /**
   * Check if token should trigger a refresh
   *
   * @param bufferMs - Buffer time before expiration
   * @returns True if token should be refreshed
   */
  static shouldRefresh(bufferMs: number = DEFAULT_EXPIRATION_BUFFER_MS): boolean {
    const status = this.getValidationStatus(bufferMs);
    return status.isExpired || status.isExpiringSoon;
  }

  /**
   * Get time until token expires in milliseconds
   *
   * @returns Milliseconds until expiration (negative if expired)
   */
  static getTimeUntilExpiry(): number {
    const token = getStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
    if (!token) {
      return -1;
    }

    const validation = validateToken(token);
    return validation.secondsUntilExpiration * 1000;
  }

  /**
   * Check if we have a valid token for making authenticated requests
   * This is a quick check that doesn't trigger any side effects
   *
   * @returns True if we have a valid, non-expired token
   */
  static hasValidToken(): boolean {
    const token = getStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
    if (!token) {
      return false;
    }

    const validation = validateToken(token, 0); // No buffer for strict check
    return validation.isValid;
  }
}

/**
 * Simple function to check token expiration before API calls
 * Can be used in any context without hooks or providers
 *
 * @param operation - Name of the operation for logging
 * @param bufferMs - Buffer time before expiration
 * @returns Promise that resolves to true if token is valid, false otherwise
 */
export async function ensureTokenValid(
  operation: string = 'API call',
  bufferMs: number = DEFAULT_EXPIRATION_BUFFER_MS
): Promise<boolean> {
  // First check if this is a provisional MFA operation
  const provisionalToken = getStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
  const operationLower = operation.toLowerCase();

  console.log(`ensureTokenValid: Debug for ${operation}:`, {
    hasProvisionalToken: !!provisionalToken,
    operationLower,
    isMfaOperation: operationLower.includes('mfa'),
    isSetupOperation: operationLower.includes('setup'),
    isVerifyOperation: operationLower.includes('verify')
  });

  if (provisionalToken && (operationLower.includes('mfa') || operationLower.includes('setup') || operationLower.includes('verify'))) {
    console.log(`ensureTokenValid: Using provisional token for MFA operation: ${operation}`);
    return true;
  }

  const status = TokenChecker.getValidationStatus(bufferMs);

  console.log(`ensureTokenValid: Checking token for ${operation}:`, {
    hasToken: status.hasToken,
    isValid: status.isValid,
    isExpired: status.isExpired,
    isExpiringSoon: status.isExpiringSoon,
    secondsUntilExpiration: status.secondsUntilExpiration,
    hasProvisionalToken: !!provisionalToken
  });

  if (!status.hasToken) {
    console.error(`ensureTokenValid: No token found for ${operation}`);
    return false;
  }

  if (status.isExpired) {
    console.error(`ensureTokenValid: Token expired for ${operation}`);

    // Dispatch session expired event
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('session-expired', {
        detail: {
          message: 'Your session has expired - please log in again',
          timestamp: new Date().toISOString(),
          operation
        }
      }));
    }

    return false;
  }

  if (status.isExpiringSoon) {
    console.warn(`ensureTokenValid: Token expires soon for ${operation} (${status.secondsUntilExpiration}s remaining)`);

    // Dispatch token expiring soon event for proactive refresh
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('token-expiring-soon', {
        detail: {
          secondsUntilExpiration: status.secondsUntilExpiration,
          operation
        }
      }));
    }
  }

  return status.isValid;
}
